import { ref, watch } from "vue";
import { toArray } from "#ce/utils/props";

/**
 * Two-way binding for the host's `selected` prop with content-aware dedupe.
 *
 * Mirrors `props.selected` into a writable `model` ref. `selectByUser` is the one
 * entry point that reports a selection: it updates the model, emits
 * `update-selected` with unwrapped values, and runs the optional `onChange`
 * callback (e.g. closeOnSelect), all skipped when the selection's content is
 * unchanged (same members + same length). Every other write — the `selected`
 * prop, a `value` property write, a form reset — assigns `model` directly and
 * stays silent, so `update-selected` reports the user's pick alone.
 *
 * @param {object} props  Owning component's reactive props; reads `props.selected`.
 * @param {Function} emit Owning component's `defineEmits` emitter.
 * @param {{ getItemValue: (item: any) => any, onChange?: (model: any[]) => void }} options
 */
export function useSelectionModel(props, emit, { getItemValue, onChange }) {
  const model = ref(toArray(props.selected));

  function selectByUser(value) {
    const previous = model.value;
    model.value = value;

    if (isUnchanged(value, previous)) {
      return;
    }

    emit("update-selected", toArray(value).map(getItemValue));
    onChange?.(value);
  }

  // Compared as unwrapped values, the shape the emit carries: `model` holds a reactive proxy, so
  // its members never match the raw items a container reports by identity.
  function isUnchanged(newValue, oldValue) {
    const next = toArray(newValue).map(getItemValue);
    const previous = toArray(oldValue).map(getItemValue);

    return next.length === previous.length && next.every((entry) => previous.includes(entry));
  }

  function syncFromProp(value) {
    model.value = toArray(value);
  }

  watch(() => props.selected, syncFromProp);

  return { model, selectByUser };
}
